/**
 * Gravador de vídeo curto para o chat — espelha o `VoiceRecorder`.
 *
 * **Por que gravar aqui em vez de aceitar da galeria.** Medido no navegador,
 * 5 minutos de vídeo: da galeria em 4K são ~1.690 MB; gravado aqui em 480p com
 * cena calma, ~3 MB. A diferença é quem escolhe a qualidade — e reduzir depois
 * exigiria transcodificar, o que não cabe nem no servidor (2 CPUs para 16
 * containers) nem no navegador (`ffmpeg.wasm` sem isolamento de origem roda em
 * uma thread só). Ver `useVideoRecorder` para os números completos.
 *
 * A interface segue a do gravador de voz de propósito: mesmo `onSend`, mesmo
 * `onActiveChange` (o compositor esconde os outros controles enquanto grava),
 * mesmo fluxo de preview-antes-de-enviar. Quem já sabe usar um sabe usar o
 * outro.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Send, Trash2, Video, VideoOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useVideoRecorder, type MotivoDeCorte } from "@/hooks/useVideoRecorder";

export interface VideoRecorderPayload {
  blob: Blob;
  mime: string;
  durationSec: number;
  sizeBytes: number;
  /** Quadro para miniatura. `null` se a captura falhou — é um extra, e a bolha
   *  cai num cartão neutro sem ele. */
  poster: Blob | null;
}

export interface VideoRecorderProps {
  onSend: (payload: VideoRecorderPayload) => void;
  disabled?: boolean;
  className?: string;
  /** O compositor esconde anexo/texto/enviar enquanto o gravador está ativo —
   *  senão a barra fica lotada demais para o polegar no celular. */
  onActiveChange?: (active: boolean) => void;
  /** Avisos ao usuário (corte automático, erro). Injetável para teste. */
  onAviso?: (mensagem: string) => void;
  enviando?: boolean;
  /** Teto de bytes. Existe porque os dois lados têm servidores com limites
   *  DIFERENTES: o endpoint do portal aceita 40 MB, o `/storage/upload` interno
   *  só 25 MB. O teto do cliente precisa ficar ABAIXO do teto do servidor — o
   *  corte dispara quando o acumulado ULTRAPASSA o valor, então igualar os dois
   *  faria o envio falhar no fim de uma gravação de 5 minutos. */
  maxBytes?: number;
  /** Inicia a contagem assim que monta.
   *
   *  Usado quando o gravador é aberto por um MENU: o usuário já escolheu
   *  "gravar vídeo", então exigir um segundo clique no ícone seria pedir a
   *  mesma intenção duas vezes. */
  iniciarAoMontar?: boolean;
}

function formatarDuracao(seg: number): string {
  const m = Math.floor(seg / 60);
  const s = seg % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatarMB(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Pausa antes de começar a gravar.
 *
 * Pedido do Augusto ao testar: "o vídeo começa a gravar imediatamente, deveria
 * esperar uns 2 segundos". Sem ela, o começo do vídeo é sempre o dedo saindo da
 * tela e a câmera ainda ajustando foco e exposição. 3 é um número que dá para
 * contar em voz alta. */
const SEGUNDOS_DE_PREPARO = 3;

const MENSAGEM_DE_CORTE: Record<MotivoDeCorte, string> = {
  duracao: "Limite de 5 minutos atingido — a gravação foi encerrada.",
  tamanho:
    "O vídeo ficou grande demais e a gravação foi encerrada. " +
    "Grave um trecho mais curto ou com menos movimento.",
};

export function VideoRecorder({
  onSend,
  disabled = false,
  className,
  onActiveChange,
  onAviso,
  enviando = false,
  maxBytes,
  iniciarAoMontar = false,
}: VideoRecorderProps) {
  const avisoRef = useRef(onAviso);
  useEffect(() => {
    avisoRef.current = onAviso;
  }, [onAviso]);

  const aoCortar = useCallback((motivo: MotivoDeCorte) => {
    // Sem este aviso o vídeo pararia sozinho e o parceiro acharia que travou —
    // e tentaria de novo, produzindo o mesmo resultado.
    avisoRef.current?.(MENSAGEM_DE_CORTE[motivo]);
  }, []);

  const {
    status,
    durationSec,
    bytesAtuais,
    error,
    recording,
    isSupported,
    streamRef,
    videoParaPosterRef,
    start,
    stop,
    cancel,
    reset,
  } = useVideoRecorder({ onCorteAutomatico: aoCortar, maxBytes });

  const previewAoVivoRef = useRef<HTMLVideoElement | null>(null);
  /** Contagem regressiva antes de gravar. `null` = não está contando.
   *
   *  Pedido do Augusto ao testar (06/08/2026): "o vídeo começa a gravar
   *  imediatamente, deveria esperar uns 2 segundos". Sem a pausa, o começo do
   *  vídeo é sempre o dedo saindo da tela e a câmera ainda se ajustando. */
  const [contagem, setContagem] = useState<number | null>(null);
  /** Mensagem quando o preview não consegue carregar/tocar.
   *
   *  Sem isto o usuário vê um retângulo PRETO com controles mortos e nenhuma
   *  explicação — foi o relato do Augusto ("não consigo dar play"). Falha
   *  silenciosa em mídia é indistinguível de app quebrado. */
  const [erroDoPreview, setErroDoPreview] = useState<string | null>(null);
  const contagemTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ativo = status === "recording" || status === "stopped" || status === "requesting";
  useEffect(() => {
    onActiveChange?.(ativo);
  }, [ativo, onActiveChange]);

  // Liga a câmera ao vivo no <video>. É o que dá ao usuário a chance de
  // enquadrar — sem isso ele grava às cegas.
  useEffect(() => {
    const el = previewAoVivoRef.current;
    if (!el || status !== "recording" || !streamRef.current) return;
    // `srcObject` LANÇA se o valor não for um MediaStream válido (stream já
    // encerrada, aparelho em estado estranho). Sem o try, a exceção sobe pelo
    // efeito, o React derruba o componente e o usuário fica com a TELA VAZIA —
    // grava, aperta parar, e não vê preview nenhum.
    //
    // A prioridade certa é: o preview ao vivo é conveniência, a GRAVAÇÃO é o
    // que importa. Falhar em mostrar a imagem não pode custar o vídeo.
    try {
      el.srcObject = streamRef.current;
      el.play().catch(() => {
        /* autoplay bloqueado não impede a gravação — só o preview */
      });
    } catch {
      /* sem preview ao vivo; a gravação segue normalmente */
    }
  }, [status, streamRef]);

  useEffect(() => {
    if (error) avisoRef.current?.(error);
  }, [error]);

  /** Conta 3…2…1 e só então inicia. A câmera é aberta ANTES da contagem para
   *  que o usuário enquadre enquanto espera — pedir a permissão depois faria a
   *  contagem correr sobre uma tela preta. */
  const iniciarComContagem = useCallback(() => {
    if (contagem !== null) return; // já contando: clique duplo não reinicia
    setErroDoPreview(null);
    setContagem(SEGUNDOS_DE_PREPARO);
    contagemTimerRef.current = setInterval(() => {
      setContagem((atual) => {
        if (atual === null) return null;
        if (atual > 1) return atual - 1;
        if (contagemTimerRef.current) clearInterval(contagemTimerRef.current);
        contagemTimerRef.current = null;
        void start();
        return null;
      });
    }, 1000);
  }, [contagem, start]);

  // Sair da tela durante a contagem não pode deixar o timer vivo — ele chamaria
  // `start()` com o componente já desmontado, ligando a câmera do nada.
  useEffect(() => {
    return () => {
      if (contagemTimerRef.current) clearInterval(contagemTimerRef.current);
    };
  }, []);

  // Aberto pelo menu: começa sozinho. O `ref` evita reiniciar a cada render.
  const jaIniciouRef = useRef(false);
  useEffect(() => {
    if (iniciarAoMontar && !jaIniciouRef.current && status === "idle") {
      jaIniciouRef.current = true;
      iniciarComContagem();
    }
  }, [iniciarAoMontar, status, iniciarComContagem]);

  if (!isSupported) return null; // aparelho sem câmera: o botão nem aparece

  // ------------------------------------------------------ contagem regressiva
  if (contagem !== null) {
    return (
      <div
        className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground"
        data-testid="video-contagem"
      >
        <span className="text-2xl font-semibold tabular-nums text-foreground">{contagem}</span>
        prepare o enquadramento…
      </div>
    );
  }

  // ---------------------------------------------------------------- ocioso
  if (status === "idle" || status === "denied" || status === "no-device" || status === "error") {
    return (
      <Button
        type="button"
        variant="outline"
        className={className ?? "h-12 w-12 shrink-0"}
        onClick={iniciarComContagem}
        disabled={disabled}
        aria-label="Gravar vídeo"
        data-testid="gravar-video"
      >
        <Video className="h-5 w-5" />
      </Button>
    );
  }

  // ----------------------------------------------------------- solicitando
  if (status === "requesting") {
    return (
      <div className="flex flex-1 items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Abrindo a câmera…
      </div>
    );
  }

  // -------------------------------------------------------------- gravando
  if (status === "recording") {
    return (
      <div className="flex flex-1 flex-col gap-2" data-testid="video-gravando">
        <video
          ref={(el) => {
            previewAoVivoRef.current = el;
            // O mesmo elemento serve de fonte para o quadro da miniatura: ele
            // já está exibindo a câmera, então desenhar dele custa um
            // `drawImage`. Ver `useVideoRecorder` para o porquê da captura ser
            // no cliente.
            videoParaPosterRef.current = el;
          }}
          muted
          playsInline
          className="w-full rounded-md bg-black"
          style={{ maxHeight: "40dvh" }}
        />
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-sm font-medium text-red-600">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-600" />
            {formatarDuracao(durationSec)}
          </span>
          {/* Mostrar os MB enquanto grava não é curiosidade: é o que explica um
              corte por tamanho antes dos 5 min. Sem isso o corte parece bug. */}
          <span className="text-xs text-muted-foreground">{formatarMB(bytesAtuais)}</span>
          <div className="flex-1" />
          <Button
            type="button"
            variant="outline"
            className="h-12 w-12 shrink-0"
            onClick={cancel}
            aria-label="Descartar gravação"
            data-testid="video-descartar"
          >
            <VideoOff className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            className="h-12 shrink-0 px-4"
            onClick={stop}
            aria-label="Parar gravação"
            data-testid="video-parar"
          >
            Parar
          </Button>
        </div>
      </div>
    );
  }

  // --------------------------------------------------- preview antes de enviar
  return (
    <div className="flex flex-1 flex-col gap-2" data-testid="video-preview">
      {recording && (
        <video
          src={recording.url}
          controls
          playsInline
          preload="metadata"
          className="w-full rounded-md bg-black"
          style={{ maxHeight: "40dvh" }}
          data-testid="video-preview-player"
          onLoadedMetadata={(e) => {
            // `MediaRecorder` costuma produzir blob com `duration: Infinity` —
            // sobretudo em WebM (o que o Android grava quando não há mp4). O
            // vídeo até carrega, mas os controles ficam sem linha do tempo e o
            // play se comporta de forma errática.
            //
            // Buscar um tempo absurdo força o navegador a percorrer o arquivo e
            // calcular a duração real; depois volta ao início. É o contorno
            // conhecido, e é barato: só roda quando a duração vem inválida.
            const v = e.currentTarget;
            if (!Number.isFinite(v.duration)) {
              const aoAtualizar = () => {
                v.removeEventListener("timeupdate", aoAtualizar);
                v.currentTime = 0;
              };
              v.addEventListener("timeupdate", aoAtualizar);
              v.currentTime = 1e101;
            }
          }}
          onError={() => {
            setErroDoPreview(
              "Não foi possível pré-visualizar o vídeo neste aparelho. " +
                "Você ainda pode enviá-lo — ele foi gravado normalmente."
            );
          }}
        />
      )}
      {erroDoPreview && (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
          data-testid="video-preview-erro"
        >
          {erroDoPreview}
        </div>
      )}
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">
          {recording
            ? `${formatarDuracao(recording.durationSec)} · ${formatarMB(recording.sizeBytes)}`
            : ""}
        </span>
        <div className="flex-1" />
        <Button
          type="button"
          variant="outline"
          className="h-12 w-12 shrink-0"
          onClick={reset}
          disabled={enviando}
          aria-label="Descartar vídeo"
          data-testid="video-descartar-preview"
        >
          <Trash2 className="h-5 w-5" />
        </Button>
        <Button
          type="button"
          className="h-12 w-12 shrink-0"
          disabled={enviando || !recording}
          onClick={() => {
            if (!recording) return;
            onSend({
              blob: recording.blob,
              mime: recording.mimeType,
              durationSec: recording.durationSec,
              sizeBytes: recording.sizeBytes,
              poster: recording.poster,
            });
            reset();
          }}
          aria-label="Enviar vídeo"
          data-testid="video-enviar"
        >
          {enviando ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
        </Button>
      </div>
    </div>
  );
}
